import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import type { Page, Report } from 'powerbi-client';
import { captureBookmarkState } from '../utils/captureBookmarkState';
import { DaapPowerBiExportMutationResult } from '../interfaces/DaapPowerBiExportMutationResult';
import { DaapPowerBiExportStatusQueryResult } from '../interfaces/DaapPowerBiExportStatusQueryResult';

export type PowerBiExportFileFormat = 'PDF' | 'PPTX';

const TERMINAL_FAILURE_STATUSES = new Set(['FAILED', 'UNKNOWN']);

const TOOLTIP_PAGE_PATTERN = /tooltip/i;

const isTooltipPage = (page: Pick<Page, 'name' | 'displayName'>) =>
  TOOLTIP_PAGE_PATTERN.test(page.name ?? '') ||
  TOOLTIP_PAGE_PATTERN.test(page.displayName ?? '');

export const DAAP_POWER_BI_EXPORT_MUTATION = gql`
  mutation DaapPowerBiExport($exportInput: DaapPowerBiExportInput!) {
    daapPowerBiExport(exportInput: $exportInput) {
      exportId
    }
  }
`;

export const DAAP_POWER_BI_EXPORT_STATUS_QUERY = gql`
  query DaapPowerBiExportStatus(
    $appName: String!
    $exportId: String!
    $exportFormat: DaapPowerBiExportFormat!
  ) {
    daapPowerBiExportStatus(
      appName: $appName
      exportId: $exportId
      exportFormat: $exportFormat
    ) {
      exportId
      status
      percentComplete
      downloadUrl
      failureReason
    }
  }
`;

export const usePowerBiExport = (appName: string) => {
  const [isExportInProgress, setIsExportInProgress] = useState(false);
  const [exportFailureReason, setExportFailureReason] = useState<
    string | undefined
  >(undefined);

  const [exportPowerBiReport] = useMutation<DaapPowerBiExportMutationResult>(
    DAAP_POWER_BI_EXPORT_MUTATION
  );

  const [fetchExportStatus, { data, stopPolling }] =
    useLazyQuery<DaapPowerBiExportStatusQueryResult>(
      DAAP_POWER_BI_EXPORT_STATUS_QUERY,
      {
        pollInterval: 1000,
        fetchPolicy: 'no-cache',
        onError: () => {
          stopPolling();
          setIsExportInProgress(false);
          setExportFailureReason('Export failed. Please try again.');
        },
      }
    );

  useEffect(() => {
    const status = data?.daapPowerBiExportStatus;
    if (!status) return;

    if (status.status === 'SUCCEEDED' && status.downloadUrl) {
      stopPolling();
      setIsExportInProgress(false);

      const anchorElement = document.createElement('a');
      anchorElement.setAttribute('href', status.downloadUrl);
      anchorElement.setAttribute('download', 'true');
      document.body.appendChild(anchorElement);
      anchorElement.click();
      document.body.removeChild(anchorElement);
      return;
    }

    if (TERMINAL_FAILURE_STATUSES.has(status.status)) {
      stopPolling();
      setIsExportInProgress(false);
      setExportFailureReason(
        status.failureReason ?? 'Export failed. Please try again.'
      );
    }
  }, [data, stopPolling]);

  const handleExport = useCallback(
    async (
      report: Report | null,
      exportFormat: PowerBiExportFileFormat,
      currentPageOnly = false
    ) => {
      if (!report) return;

      setIsExportInProgress(true);
      setExportFailureReason(undefined);
      try {
        const bookmarkState = await captureBookmarkState(report);
        const pageName = currentPageOnly
          ? (await report.getActivePage()).name
          : undefined;
        // Power BI's export API only includes visible pages when `pages` is
        // omitted, silently dropping pages marked HiddenInViewMode. Passing
        // every page name explicitly forces hidden pages into the export too,
        // so tooltip pages have to be filtered back out by name.
        const pageNames = currentPageOnly
          ? undefined
          : (await report.getPages())
              .filter((page) => !isTooltipPage(page))
              .map((page) => page.name);

        const { data: mutationData } = await exportPowerBiReport({
          variables: {
            exportInput: {
              appName,
              bookmarkState: bookmarkState ?? '',
              exportFormat,
              pageName,
              pageNames,
            },
          },
        });

        const exportId = mutationData?.daapPowerBiExport.exportId;
        if (!exportId) {
          throw new Error('Export failed to start.');
        }

        fetchExportStatus({ variables: { appName, exportId, exportFormat } });
      } catch (err) {
        setIsExportInProgress(false);
        throw err;
      }
    },
    [appName, exportPowerBiReport, fetchExportStatus]
  );

  return {
    handleExport,
    isExportInProgress,
    exportFailureReason,
    // PowerBI's own export job rarely reports meaningful intermediate
    // progress, so this floors at 5 to match spins-portal's behavior.
    exportProgress: Math.max(
      data?.daapPowerBiExportStatus.percentComplete ?? 0,
      5
    ),
  };
};
